import { UtmParameters } from '@types';
import { PageName } from './utils/getPageNameFromPath';
import { QuestionType } from '@qmd-types/intakeForms';

export enum Events {
  ACCOUNT_CREATED_FRONTEND = 'Account Created - Frontend',
  AGREE_TO_ALL_CONSENT_FORMS = 'Agree To All Consent Forms',
  APPLY_REFERRAL_CODE_ERROR = 'Apply Referral Code Error',
  APPLY_REFERRAL_CODE_SUCCESS = 'Apply Referral Code Success',
  APPOINTMENT_TIMES = 'Appointment Times',
  BOOKING_CREATED_FRONTEND = 'Booking Created - Frontend',
  CHANGE_DATE = 'Change Date',
  CHAT_WIDGET_OPENED = 'Chat Widget Opened',
  CHECK_CONSENT_FORMS = 'Check Consent Forms',
  CHECK_HIPAA_CONSENT_FORM = 'Check HIPAA Consent Forms',
  CLEAR_PROVIDER = 'Clear Provider',
  CLICK_APPLY_REFERRAL_CODE = 'Click Apply Referral Code',
  CLICK_EDIT_VISIT_TYPE = 'Click Edit Visit Type',
  CLICK_NOT_READY_TO_BOOK = 'Click Not Ready To Book',
  CLICK_RESCHEDULE = 'Click Reschedule',
  CLICK_RESEND = 'Click Resend',
  CLICK_SEE_ALL_APPOINTMENTS = 'Click See All Appointments',
  CLOSE_INTAKE_RESPONSE = 'Close Intake Response',
  CONFIRM_REMOVE_REFERRAL_CODE = 'Confirm Remove Referral Code',
  DECLINE_INCOMING_VISIT_CALL = 'Decline Incoming Visit Call',
  EDIT_INTAKE_RESPONSE = 'Edit Intake Response',
  EMAIL_EXISTS = 'Email Exists',
  EXPERIMENT_STARTED = 'Experiment Started',
  FILTER_BY_HOUR = 'Filter By Hour',
  FILTER_BY_TIME_OF_DAY = 'Filter By Time Of Day',
  GO_BACK = 'Go Back',
  GO_TO_APP = 'Go To App',
  GO_TO_INTAKE_FORM = 'Go To Intake Form',
  GO_TO_LOGIN_WITH_EXISTING_EMAIL = 'Go To Login With Existing Email',
  ID_VERIFICATION_COMPLETED = 'ID Verification Completed',
  ID_VERIFICATION_STARTED = 'ID Verification Started',
  JOIN_INCOMING_VISIT_CALL = 'Join Incoming Visit Call',
  INTAKE_QUIZ_FLOW_ABANDONED = 'Intake Quiz Flow Abandoned',
  INTAKE_QUIZ_FLOW_COMPLETED = 'Intake Quiz Flow Completed',
  ONBOARDING_COMPLETED_FRONTEND = 'Onboarding Completed - Frontend',
  ONBOARDING_STARTED = 'Onboarding Started',
  OPEN_APPLY_REFERRAL_CODE_POPUP = 'Open Apply Referral Code Popup',
  OPEN_REMOVE_REFERRAL_CODE_POPUP = 'Open Remove Referral Code Popup',
  PATIENT_UPDATED_PREFERRED_PHARMACY = 'Patient Updated Preferred Pharmacy',
  REDIRECT_TO_MHT_WAIT_LIST = 'Redirect to MHT Wait List',
  SAVE_INTAKE_RESPONSE = 'Save Intake Response',
  SAVE_PAYMENT_METHOD = 'Save Payment Method',
  SEARCH_PROVIDERS = 'Search Providers',
  SELECT_APPOINTMENT_SLOT = 'Select Appointment Slot',
  SELECT_PROVIDER = 'Select Provider',
  SELECT_SERVICE = 'Select Service',
  SELECT_STATE = 'Select State',
  SELECT_TIME_BLOCK = 'Select Time Block',
  SELECT_VIEW_ALL_AVAILABILITIES = 'Select View All Availabilities',
  SELECT_VISIT = 'Select Visit',
  SELECTED_BACK = 'Selected Back',
  SELECTED_SIGN_IN = 'Selected Sign In',
  SUBMIT_INTAKE_FORM = 'Submit Intake Form',
  SUBMIT_INTAKE_FORM_QUESTION = 'Submit Intake Form Question',
  TOGGLE_FILTERS = 'Toggle Filters',
  TOGGLE_PROVIDER_CARD = 'Toggle Provider Card',
  VIEW_COMPLETED_INTAKE_FORM = 'View Completed Intake Form',
  VIEW_INCOMING_VISIT_CALL_ALERT = 'View Incoming Visit Call Alert',
  VIEW_INTAKE_FORM_QUESTION = 'View Intake Form Question',
  VIEW_REVIEW_INTAKE_FORM = 'View Review Intake Form',
  VIEW_TIME_BLOCKS = 'View Time Blocks',
  VIEWED_INTERSTITIAL = 'Viewed Interstitial',
}

export interface BaseProperties {
  flow: string;
  selectedState?: string;
  serviceTypeId?: string;
}

export const baseProperties: BaseProperties = {
  flow: 'Onboarding',
};

export type QuizSessionProperties = {
  screens_completed: number;
  session_id: string;
};

export enum EventAction {
  OPEN = 'Open',
  CLOSE = 'Close',
}

export enum EventDirection {
  NEXT = 'Next',
  PREV = 'Prev',
}

export interface EventProperties {
  [Events.ACCOUNT_CREATED_FRONTEND]: UtmParameters & {
    firstName: string;
    lastName: string;
    mrn: string;
    email: string;
    patientId: string;
    phone?: string;
  };
  [Events.AGREE_TO_ALL_CONSENT_FORMS]: null;
  [Events.APPLY_REFERRAL_CODE_ERROR]: {
    errorCode: string;
  };
  [Events.APPLY_REFERRAL_CODE_SUCCESS]: {
    referralCode: string;
  };
  [Events.APPOINTMENT_TIMES]: {
    state: string;
    appointmentsBy: string;
    timeToEarliestAvailableAppointment?: number;
    noAppointmentAvailable: boolean;
    numberOfProviders?: number;
    serviceType: string;
    visitType: string;
    hasVisits: boolean;
    earliestAvailableAppointmentWithin15min?: boolean;
    earliestAvailableAppointmentWithin30min?: boolean;
    earliestAvailableAppointmentWithin60min?: boolean;
    earliestAvailableAppointmentWithin120min?: boolean;
    earliestAvailableAppointmentWithin240min?: boolean;
    earliestAvailableAppointmentPast240min?: boolean;
    daysAwayAvailable?: number;
    dayOfWeekNumber?: number;
    dayOfWeekName?: string;
    dayOfWeekNumberName?: string;
    timezoneOffset?: number;
    providersPast240min?: number;
    providersWithin15min?: number;
    providersWithin30min?: number;
    providersWithin60min?: number;
    providersWithin120min?: number;
    providersWithin240min?: number;
    timeSlotsPast240min?: number;
    timeSlotsWithin15min?: number;
    timeSlotsWithin30min?: number;
    timeSlotsWithin60min?: number;
    timeSlotsWithin120min?: number;
    timeSlotsWithin240min?: number;
  };
  [Events.BOOKING_CREATED_FRONTEND]: UtmParameters & {
    serviceType: string;
    state: string;
    timeOfAppointment: string;
    visitType: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
    type: string;
    hasVisits: boolean;
    isReferral: boolean;
    visitId: string;
  };
  [Events.CHANGE_DATE]: {
    Direction: EventDirection;
  };
  [Events.CHAT_WIDGET_OPENED]: { url: string };
  [Events.CHECK_CONSENT_FORMS]: null;
  [Events.CHECK_HIPAA_CONSENT_FORM]: null;
  [Events.CLEAR_PROVIDER]: null;
  [Events.CLICK_APPLY_REFERRAL_CODE]: null;
  [Events.CLICK_EDIT_VISIT_TYPE]: null;
  [Events.CLICK_NOT_READY_TO_BOOK]: UtmParameters;
  [Events.CLICK_RESCHEDULE]: null;
  [Events.CLICK_RESEND]: null;
  [Events.CLICK_SEE_ALL_APPOINTMENTS]: {
    timeBlock: 'now' | 'today' | 'tomorrow' | null;
  };
  [Events.CLOSE_INTAKE_RESPONSE]: null;
  [Events.CONFIRM_REMOVE_REFERRAL_CODE]: null;
  [Events.DECLINE_INCOMING_VISIT_CALL]: null;
  [Events.EDIT_INTAKE_RESPONSE]: {
    QuestionText: string | null;
    QuestionType: QuestionType;
  };
  [Events.EMAIL_EXISTS]: null;
  [Events.EXPERIMENT_STARTED]: {
    experimentName: ExperimentNames;
    variantName: string;
  };
  [Events.FILTER_BY_HOUR]: {
    Time: number; // Hour of the day in 24-hour format (e.g., 6 for 6:00 AM, 16 for 4:00 PM)
  };
  [Events.FILTER_BY_TIME_OF_DAY]: {
    Range: 'Morning' | 'Afternoon' | 'Evening';
  };
  [Events.GO_BACK]: {
    page?: PageName;
  };
  [Events.GO_TO_APP]: {
    Timeout: boolean;
  };
  [Events.GO_TO_INTAKE_FORM]: {
    ServiceType?: string;
    VisitType?: string;
  };
  [Events.GO_TO_LOGIN_WITH_EXISTING_EMAIL]: null;
  [Events.ID_VERIFICATION_COMPLETED]: {
    patientId?: string;
    jobId?: string;
    success?: boolean;
  };
  [Events.ID_VERIFICATION_STARTED]: {
    patientId?: string;
  };
  [Events.JOIN_INCOMING_VISIT_CALL]: null;
  [Events.INTAKE_QUIZ_FLOW_ABANDONED]: {
    lastQuestionId: string | null;
    answeredCount: number;
  };
  [Events.INTAKE_QUIZ_FLOW_COMPLETED]: {
    answeredCount: number;
  };
  [Events.ONBOARDING_COMPLETED_FRONTEND]: UtmParameters & {
    serviceType: string;
    state: string;
    timeOfAppointment: string;
    visitType: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
    type: string;
    hasVisits: boolean;
    visitId: string;
    isReferral: boolean;
  };
  [Events.ONBOARDING_STARTED]: UtmParameters;
  [Events.OPEN_APPLY_REFERRAL_CODE_POPUP]: null;
  [Events.OPEN_REMOVE_REFERRAL_CODE_POPUP]: null;
  [Events.PATIENT_UPDATED_PREFERRED_PHARMACY]: {
    pharmacyName: string;
    pharmacyId: string;
    location: 'Intake Form' | 'Profile';
  };
  [Events.REDIRECT_TO_MHT_WAIT_LIST]: {
    state: string;
  };
  [Events.SAVE_INTAKE_RESPONSE]: {
    QuestionType: QuestionType;
    QuestionText: string | null;
  };
  [Events.SAVE_PAYMENT_METHOD]: null;
  [Events.SEARCH_PROVIDERS]: {
    Input: string;
  };
  [Events.SELECT_APPOINTMENT_SLOT]: {
    minutesUntilAppointment: number;
    slotPosition: 'top' | 'middle' | 'bottom';
    timeBlock: 'now' | 'today' | 'tomorrow' | null;
  };
  [Events.SELECT_PROVIDER]: {
    Provider: string;
  };
  [Events.SELECT_SERVICE]: {
    ServiceType: string;
  };
  [Events.SELECT_STATE]: {
    AutoSelected: boolean;
  };
  [Events.SELECT_TIME_BLOCK]: {
    availableTimeBlocks: {
      now: boolean;
      today: boolean;
      tomorrow: boolean;
    };
    timeBlock: 'now' | 'today' | 'tomorrow' | null;
  };
  [Events.SELECT_VIEW_ALL_AVAILABILITIES]: {
    minutesUntilAppointment?: number;
    Provider: string;
  };
  [Events.SELECT_VISIT]: {
    DoctorPosition?: number;
  };
  [Events.SELECTED_BACK]: QuizSessionProperties & {
    screen_name: string;
  };
  [Events.SELECTED_SIGN_IN]: QuizSessionProperties & {
    screen_name: string;
  };
  [Events.SUBMIT_INTAKE_FORM]: null;
  [Events.SUBMIT_INTAKE_FORM_QUESTION]: {
    QuestionType: QuestionType;
    QuestionText: string | null;
  };
  [Events.TOGGLE_FILTERS]: {
    Action: EventAction;
  };
  [Events.TOGGLE_PROVIDER_CARD]: {
    Action: EventAction;
  };
  [Events.VIEW_COMPLETED_INTAKE_FORM]: null;
  [Events.VIEW_INCOMING_VISIT_CALL_ALERT]: null;
  [Events.VIEW_INTAKE_FORM_QUESTION]: {
    QuestionType: QuestionType;
    QuestionText: string | null;
  };
  [Events.VIEW_REVIEW_INTAKE_FORM]: null;
  [Events.VIEW_TIME_BLOCKS]: {
    availableTimeBlocks: {
      now: boolean;
      today: boolean;
      tomorrow: boolean;
    };
  };
  [Events.VIEWED_INTERSTITIAL]: QuizSessionProperties;
}

export type ExperimentNames = 'curated_appointment_slots' | 'mat_questions';
